import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { 
  withRouteSplitting, 
  ProtectedRoute, 
  RouteTransition,
  useRouteMetadata,
  useRouteAnalytics,
  useScrollRestoration
} from './utils/routing';

// Core components
import NavBar from './components/NavBar';
import Banner from './components/Banner';
import ErrorBoundary from './components/ErrorBoundary';
import ScorekeeperInterface from './components/ScorekeeperInterface';
import RequireAdmin from './components/RequireAdmin';
import RewardNotifier from './components/RewardNotifier';
import AssessmentGoalsNotifier from './components/AssessmentGoalsNotifier';
import AssessmentGoalResultNotifier from './components/AssessmentGoalResultNotifier';
import MilestoneModal from './components/MilestoneModal';
import GeneratorEarningsModal from './components/GeneratorEarningsModal';
import AnnouncementCarousel from './components/AnnouncementCarousel';
import SquadCheckInReminderModal, { shouldShowSquadCheckInReminder } from './components/SquadCheckInReminderModal';
import SkillLoadoutTutorialModal from './components/SkillLoadoutTutorialModal';
import { hasSeenSkillLoadoutTutorial } from './utils/skillLoadoutTutorial';
import { shouldShowModalToday } from './utils/generatorEarnings';
import { getActiveAnnouncements, ROLLOUT_ANNOUNCEMENTS } from './utils/announcementConfig';
import { checkAndCreditDailyGenerator, shouldShowDailyGeneratorModal } from './utils/dailyGeneratorNotification';
import { db } from './firebase';
import { doc, getDoc, setDoc, Timestamp, serverTimestamp } from 'firebase/firestore';

// Context providers
import { AuthProvider, useAuth } from './context/AuthContext';
import { LevelUpProvider } from './context/LevelUpContext';
import { BattleProvider, useBattle } from './context/BattleContext';
import { StoryProvider } from './context/StoryContext';
import { MilestoneProvider, useMilestone } from './context/MilestoneContext';
import { ToastProvider } from './context/ToastContext';

// Development components
import FirebaseStatus from './components/FirebaseStatus';
import TutorialManager from './components/TutorialManager';
import InvitationManager from './components/InvitationManager';
import BattleInvitationManager from './components/BattleInvitationManager';
import NavigationDebugger from './components/NavigationDebugger';
import InSessionNotification from './components/InSessionNotification';
import ToastContainer from './components/ToastContainer';
import SkillActivationOverlay from './components/skillCard/SkillActivationOverlay';
import { useDailyChallengeToasts } from './hooks/useDailyChallengeToasts';
import { usePopupControls } from './hooks/usePopupControls';

// Load debug commands
import './utils/consoleCommands';

// Suppress Firestore internal assertion errors globally - MUST run after imports
function buildErrorDiagnosticString(value: any): string {
  if (value == null) return '';
  const parts: string[] = [
    String(value),
    typeof value === 'object' && value?.message ? String(value.message) : '',
    typeof value === 'object' && value?.stack ? String(value.stack) : '',
    typeof value === 'object' && value?.code != null ? String(value.code) : ''
  ];
  try {
    if (typeof value === 'object') {
      parts.push(JSON.stringify(value));
      if (value.context) parts.push(JSON.stringify(value.context));
    }
  } catch (_) {
    // ignore
  }
  return parts.join(' ');
}

const isFirestoreInternalError = (error: any): boolean => {
  const allErrorStrings = buildErrorDiagnosticString(error);
  if (!allErrorStrings.length) return false;
  return (
    allErrorStrings.includes('INTERNAL ASSERTION FAILED') ||
    allErrorStrings.includes('ID: ca9') ||
    allErrorStrings.includes('(ID: ca9)') ||
    allErrorStrings.includes('ca9') ||
    allErrorStrings.includes('ID: b815') ||
    allErrorStrings.includes('b815') ||
    (allErrorStrings.includes('FIRESTORE') && allErrorStrings.includes('Unexpected state')) ||
    (allErrorStrings.includes('FIRESTORE') && allErrorStrings.includes('INTERNAL ASSERTION')) ||
    allErrorStrings.includes('__PRIVATE__fail') ||
    allErrorStrings.includes('__PRIVATE_hardAssert') ||
    allErrorStrings.includes('__PRIVATE_WatchChangeAggregator') ||
    allErrorStrings.includes('__PRIVATE_PersistentListenStream') ||
    allErrorStrings.includes('BrowserConnectivityMonitor') ||
    (allErrorStrings.includes('FIRESTORE') && allErrorStrings.includes('(11.10.0)')) ||
    (allErrorStrings.includes('CONTEXT') && (allErrorStrings.includes('"ve":-1') || allErrorStrings.includes('ve":-1')))
  );
};

function shouldSuppressFirestoreError(event: ErrorEvent | PromiseRejectionEvent): boolean {
  try {
    if ('error' in event && isFirestoreInternalError((event as ErrorEvent).error)) return true;
    if ('message' in event && isFirestoreInternalError((event as ErrorEvent).message)) return true;
    if ('reason' in event && isFirestoreInternalError((event as PromiseRejectionEvent).reason)) return true;
    return false;
  } catch (_) {
    return false;
  }
}

// Override console.error immediately to catch Firestore errors
const originalConsoleError = console.error;
console.error = function (...args: any[]) {
  const message = args.join(' ');
  if (isFirestoreInternalError(message) || args.some((arg) => isFirestoreInternalError(arg))) {
    return;
  }
  originalConsoleError.apply(console, args);
};

// Global error handlers - capture phase first so we run before React's overlay
window.addEventListener(
  'error',
  (event: ErrorEvent) => {
    if (shouldSuppressFirestoreError(event)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  },
  true
);

window.addEventListener(
  'error',
  (event: ErrorEvent) => {
    if (shouldSuppressFirestoreError(event)) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  },
  false
);

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  if (shouldSuppressFirestoreError(event)) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
});

// Lazy load pages for better performance with route splitting
const Dashboard = withRouteSplitting(() => import('./pages/Dashboard'));
const Home = withRouteSplitting(() => import('./pages/Home'));
const Profile = withRouteSplitting(() => import('./pages/Profile'));
const SkillTree = withRouteSplitting(() => import('./pages/SkillTree'));
const Login = withRouteSplitting(() => import('./pages/Login'));
const PasswordReset = withRouteSplitting(() => import('./pages/PasswordReset'));
const AdminPanel = withRouteSplitting(() => import('./pages/AdminPanel'));
const Marketplace = withRouteSplitting(() => import('./pages/Marketplace'));
const Leaderboard = withRouteSplitting(() => import('./pages/Leaderboard'));
const Chapters = withRouteSplitting(() => import('./pages/Chapters'));
const Squads = withRouteSplitting(() => import('./pages/Squads'));
const Battle = withRouteSplitting(() => import('./pages/Battle'));
const Artifacts = withRouteSplitting(() => import('./pages/Artifacts'));
const Story = withRouteSplitting(() => import('./pages/Story'));
const StoryEpisodeBattle = withRouteSplitting(() => import('./pages/StoryEpisodeBattle'));
const IslandRaid = withRouteSplitting(() => import('./pages/IslandRun'));
const IslandRaidLobby = withRouteSplitting(() => import('./components/IslandRunLobby'));
const IslandRaidGame = withRouteSplitting(() => import('./components/IslandRaidGame'));
const InSession = withRouteSplitting(() => import('./pages/InSession'));
const LiveEvents = withRouteSplitting(() => import('./pages/LiveEvents'));
const InSessionRoom = withRouteSplitting(() => import('./components/InSessionRoom'));
const InSessionCreate = withRouteSplitting(() => import('./components/InSessionCreate'));
const InSessionBattleView = withRouteSplitting(() => import('./components/InSessionBattleView'));
const AssessmentGoalsStudent = withRouteSplitting(() => import('./components/AssessmentGoalsStudent'));
const WeeklyGoalsPage = withRouteSplitting(() => import('./pages/WeeklyGoalsPage'));
const WorkBoardPage = withRouteSplitting(() => import('./pages/WorkBoardPage'));
const TrainingGrounds = withRouteSplitting(() => import('./pages/TrainingGrounds'));
const SkillLibraryAdmin = withRouteSplitting(() => import('./pages/admin/SkillLibraryAdmin'));
const SkillAnalyticsAdmin = withRouteSplitting(() => import('./pages/admin/SkillAnalyticsAdmin'));
const MissionRunner = withRouteSplitting(() => import('./pages/MissionRunner'));
const Level2ManifestBuilderPage = withRouteSplitting(() => import('./pages/Level2ManifestBuilderPage'));
const QuizPlayer = withRouteSplitting(() => import('./pages/QuizPlayer'));
const QuizResults = withRouteSplitting(() => import('./pages/QuizResults'));
const BattlePassSeasonPage = withRouteSplitting(() => import('./pages/BattlePassSeasonPage'));
const EnergyMasteryPage = withRouteSplitting(() => import('./pages/EnergyMasteryPage'));
const ManifestSkillEvolutionPage = withRouteSplitting(() => import('./pages/ManifestSkillEvolutionPage'));

// Loading component for lazy-loaded routes
const PageLoader = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50vh',
    gap: '1rem'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '4px solid #e5e7eb',
      borderTop: '4px solid #4f46e5',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}></div>
    <p style={{ color: '#6b7280', fontSize: '1rem' }}>Loading...</p>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// Error handlers already set up at the top of the file

// Protected Admin Route Component
const ProtectedAdminRoute = () => {
  const { currentUser, loading, isAdmin } = useAuth();
  
  // Check if current user is admin - Only Yondaime has access
  const isAdminUser = isAdmin || currentUser?.email === 'edm21179@gmail.com';

  // Initial auth only — never bounce away once we know this session is admin
  // (transient null currentUser on token refresh must not remount Admin / wipe forms)
  if (loading && !currentUser && !isAdminUser) {
    return <PageLoader />;
  }
  
  if (!currentUser && !isAdminUser) {
    return <Navigate to="/login" replace />;
  }
  
  if (currentUser && !isAdminUser) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        gap: '1rem',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '4rem' }}>🚫</div>
        <h2 style={{ color: '#ef4444', margin: 0 }}>Access Denied</h2>
        <p style={{ color: '#6b7280', margin: 0 }}>
          You don't have permission to access the admin panel.
        </p>
        <button
          onClick={() => window.history.back()}
          style={{
            backgroundColor: '#4f46e5',
            color: 'white',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500',
            marginTop: '1rem'
          }}
        >
          Go Back
        </button>
      </div>
    );
  }
  
  return (
    <Suspense fallback={<PageLoader />}>
      <AdminPanel />
    </Suspense>
  );
};

// App content component with routing utilities
const AppContent = () => {
  const { currentUser, loading } = useAuth();
  const { showMilestone, currentMilestone, isOpen, closeMilestone } = useMilestone();
  const { updateMetadata } = useRouteMetadata();
  useRouteAnalytics();
  useScrollRestoration();
  
  // Global hook to detect daily challenge completions and show toasts
  useDailyChallengeToasts();
  const { controls: popupControls, loading: popupControlsLoading } = usePopupControls();
  
  // Generator earnings modal state
  const [generatorEarnings, setGeneratorEarnings] = React.useState<{
    daysAway: number;
    ppEarned: number;
    shieldsEarned: number;
    generatorLevel: number;
    ppPerDay: number;
    shieldsPerDay: number;
    previousPP: number;
    previousShields: number;
    newPP: number;
    newShields: number;
  } | null>(null);
  const [showGeneratorModal, setShowGeneratorModal] = React.useState(false);
  
  // Announcement carousel state
  const [showAnnouncementCarousel, setShowAnnouncementCarousel] = React.useState(false);
  const [announcementTypes, setAnnouncementTypes] = React.useState<Array<'chapter2'>>([]);
  
  // Modal queue: ['dailyGenerator', 'announcements']
  const [modalQueue, setModalQueue] = React.useState<string[]>([]);
  const [currentModal, setCurrentModal] = React.useState<string | null>(null);
  /** Prevents re-queuing login modals when vault / rates change after first successful check. */
  const loginModalsCheckedForUidRef = React.useRef<string | null>(null);
  const loginModalsInFlightRef = React.useRef(false);
  
  // Get battle context for generator rates
  const { vault, getGeneratorRates } = useBattle();

  // Check for daily generator notification and announcements on app load
  React.useEffect(() => {
    if (!currentUser || loading || !vault || popupControlsLoading) {
      return;
    }
    if (loginModalsCheckedForUidRef.current === currentUser.uid) {
      return;
    }
    if (loginModalsInFlightRef.current) {
      return;
    }

    const checkModals = async () => {
      loginModalsInFlightRef.current = true;
      try {
        const queue: string[] = [];
        
        // 1. Check daily generator notification (show first)
        const usersRef = doc(db, 'users', currentUser.uid);
        const usersDoc = await getDoc(usersRef);
        const usersData = usersDoc.exists() ? usersDoc.data() : {};
        
        const lastModalDate = usersData.lastDailyGeneratorModalDate || null;
        if (popupControls.enableDailyGeneratorModal && shouldShowDailyGeneratorModal(lastModalDate)) {
          const generatorLevel = vault.generatorLevel || 1;
          const rates = getGeneratorRates(generatorLevel);
          
          const dailyResult = await checkAndCreditDailyGenerator(
            currentUser.uid,
            generatorLevel,
            rates.ppPerDay,
            rates.shieldsPerDay,
            vault.capacity || 1000,
            vault.maxShieldStrength || 100
          );
          
          // Always show modal on first login of the day (even if no earnings)
          if (dailyResult) {
            setGeneratorEarnings({
              daysAway: dailyResult.daysAway,
              ppEarned: dailyResult.ppEarned,
              shieldsEarned: dailyResult.shieldsEarned,
              generatorLevel: dailyResult.generatorLevel,
              ppPerDay: dailyResult.ppPerDay,
              shieldsPerDay: dailyResult.shieldsPerDay,
              previousPP: dailyResult.previousPP,
              previousShields: dailyResult.previousShields,
              newPP: dailyResult.newPP,
              newShields: dailyResult.newShields
            });
            queue.push('dailyGenerator');
          }
        }
        
        // 2. Check announcements (show after daily generator)
        if (popupControls.enableChapter2Announcement) {
          const seenAnnouncements = usersData.seenAnnouncements || {};
          const activeAnnouncements = getActiveAnnouncements();
          const chapter2AnnouncementId = ROLLOUT_ANNOUNCEMENTS.CHAPTER2_PARTIAL_OPEN;
          
          const pendingAnnouncements: Array<'chapter2'> = [];
          if (activeAnnouncements.includes(chapter2AnnouncementId) && 
              !seenAnnouncements[chapter2AnnouncementId]) {
            pendingAnnouncements.push('chapter2');
          }
          
          if (pendingAnnouncements.length > 0) {
            setAnnouncementTypes(pendingAnnouncements);
            queue.push('announcements');
          }
        }
        
        // 3. Squad check-in reminder (first login of the day)
        if (popupControls.enableSquadCheckInReminder && shouldShowSquadCheckInReminder(currentUser.uid)) {
          queue.push('squadCheckIn');
        }

        // 4. One-time Skill Loadout tutorial (existing players who haven't seen it)
        if (popupControls.enableSkillLoadoutTutorial) {
          const seenSkillLoadout = await hasSeenSkillLoadoutTutorial(currentUser.uid);
          if (!seenSkillLoadout) {
            queue.push('skillLoadoutTutorial');
          }
        }
        
        // Set queue and show first modal
        if (queue.length > 0) {
          setModalQueue(queue);
          setCurrentModal(queue[0]);
        }

        loginModalsCheckedForUidRef.current = currentUser.uid;
      } catch (error) {
        console.error('Error checking modals:', error);
      } finally {
        loginModalsInFlightRef.current = false;
      }
    };

    // Small delay to ensure vault is fully loaded
    const timeoutId = setTimeout(checkModals, 1000);
    return () => clearTimeout(timeoutId);
  }, [currentUser, loading, vault, getGeneratorRates, popupControls, popupControlsLoading]);

  // Reset login-modal gate when the signed-in user changes
  React.useEffect(() => {
    loginModalsCheckedForUidRef.current = null;
    loginModalsInFlightRef.current = false;
  }, [currentUser?.uid]);
  
  // Handle modal queue progression
  const [showSquadCheckInModal, setShowSquadCheckInModal] = React.useState(false);
  const [skillLoadoutTutorialSource, setSkillLoadoutTutorialSource] = React.useState<'queue' | 'review' | null>(null);
  React.useEffect(() => {
    if (currentModal === 'dailyGenerator') {
      setShowGeneratorModal(true);
    } else if (currentModal === 'announcements') {
      setShowAnnouncementCarousel(true);
    } else if (currentModal === 'squadCheckIn') {
      setShowSquadCheckInModal(true);
    } else if (currentModal === 'skillLoadoutTutorial') {
      setSkillLoadoutTutorialSource('queue');
    }
  }, [currentModal]);

  // Open Skill Loadout tutorial from Help / Tutorials (Review)
  React.useEffect(() => {
    const handler = () => setSkillLoadoutTutorialSource('review');
    window.addEventListener('openSkillLoadoutTutorial', handler);
    return () => window.removeEventListener('openSkillLoadoutTutorial', handler);
  }, []);
  
  // Handle modal close - move to next in queue
  const handleModalClose = async (modalType: string) => {
    if (modalType === 'dailyGenerator') {
      setShowGeneratorModal(false);
    } else if (modalType === 'announcements') {
      setShowAnnouncementCarousel(false);
    } else if (modalType === 'squadCheckIn') {
      setShowSquadCheckInModal(false);
    } else if (modalType === 'skillLoadoutTutorial') {
      setSkillLoadoutTutorialSource(null);
    }
    
    // Move to next modal in queue
    setModalQueue(prev => {
      const nextQueue = prev.filter(m => m !== modalType);
      if (nextQueue.length > 0) {
        setCurrentModal(nextQueue[0]);
      } else {
        setCurrentModal(null);
      }
      return nextQueue;
    });
  };
  
  // Handle announcement seen — merge so new accounts without a users doc still persist
  const handleAnnouncementSeen = async (announcementId: string) => {
    if (!currentUser) return;
    
    try {
      const usersRef = doc(db, 'users', currentUser.uid);
      const usersDoc = await getDoc(usersRef);
      const usersData = usersDoc.exists() ? usersDoc.data() : {};
      const seenAnnouncements = usersData.seenAnnouncements || {};
      
      await setDoc(
        usersRef,
        {
          seenAnnouncements: {
            ...seenAnnouncements,
            [announcementId]: true
          }
        },
        { merge: true }
      );
    } catch (error) {
      console.error('Error marking announcement as seen:', error);
    }
  };

  // Listen for milestone events
  React.useEffect(() => {
    const handleMilestoneReached = (event: CustomEvent) => {
      const { milestone, moveName, rewards } = event.detail;
      showMilestone({
        milestone,
        moveName,
        rewards
      });
    };

    window.addEventListener('milestoneReached', handleMilestoneReached as EventListener);
    
    return () => {
      window.removeEventListener('milestoneReached', handleMilestoneReached as EventListener);
    };
  }, [showMilestone]);

  // Update metadata for different routes
  React.useEffect(() => {
    const path = window.location.pathname;
    const routeMetadata = {
      '/': { title: 'Dashboard', description: 'Your manifestation journey begins here' },
      '/profile': { title: 'My Profile', description: 'View and manage your manifestation profile' },
      '/chapters': { title: "Player's Journey", description: 'Explore your story chapters and challenges' },
      '/story': { title: 'Story Mode', description: 'Your journey through the Nine Knowings Universe' },
      '/battle': { title: 'Battle Arena', description: 'Engage in MST battles and challenges' },
      '/leaderboard': { title: 'Hall of Fame', description: 'See the top manifesters' },
      '/marketplace': { title: 'MST MKT', description: 'Browse and purchase artifacts' },
      '/squads': { title: 'Squads', description: 'Manage your team and collaborations' },
      '/admin': { title: "Sage's Chamber", description: 'Administrative panel' },
      '/scorekeeper': { title: 'Scorekeeper', description: 'Manage class power points' },
      '/assessment-goals': { title: 'Assessment Goals', description: 'Set goals for tests and exams' },
      '/weekly-goals': { title: 'Weekly Goals', description: 'Live Event weekly goals and progress' }
    };

    const metadata = routeMetadata[path as keyof typeof routeMetadata];
    if (metadata) {
      updateMetadata(metadata);
    }
  }, [updateMetadata]);

  // Only block the route tree on the *initial* auth check. Unmounting <Routes>
  // whenever `loading` flips (e.g. AuthProvider remount / Fast Refresh) wiped
  // in-page state and made the app feel like it "reset" to home/default views.
  const [authReady, setAuthReady] = React.useState(!loading);
  React.useEffect(() => {
    if (!loading) setAuthReady(true);
  }, [loading]);

  if (!authReady) {
    return (
      <div className="App mst-app mst-app-shell" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <NavBar />
        <Banner />
        <main className="mst-main" style={{ flex: 1 }}>
          <PageLoader />
        </main>
      </div>
    );
  }

  return (
    <div className="App mst-app mst-app-shell" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <NavBar />
      <Banner />
      <main className="mst-main" style={{ flex: 1 }}>
        <RouteTransition>
          <Routes>
            <Route path="/" element={
              currentUser ? <Navigate to="/home" replace /> : <Navigate to="/login" replace />
            } />
            <Route path="/home" element={
              <ProtectedRoute user={true}>
                <Home />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute user={true}>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/battle-pass" element={
              <ProtectedRoute user={true}>
                <BattlePassSeasonPage />
              </ProtectedRoute>
            } />
            <Route path="/energy-mastery" element={
              <ProtectedRoute user={true}>
                <EnergyMasteryPage />
              </ProtectedRoute>
            } />
            <Route path="/manifest-evolution" element={
              <ProtectedRoute user={true}>
                <ManifestSkillEvolutionPage />
              </ProtectedRoute>
            } />
            <Route path="/skill-tree" element={
              <ProtectedRoute user={true}>
                <SkillTree />
              </ProtectedRoute>
            } />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<PasswordReset />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/admin" element={
              <RequireAdmin>
                <ProtectedAdminRoute />
              </RequireAdmin>
            } />
            <Route
              path="/admin/rr-candies"
              element={
                <RequireAdmin>
                  <Navigate to="/admin?tab=rr-candies" replace />
                </RequireAdmin>
              }
            />
            <Route
              path="/admin/productivity"
              element={
                <RequireAdmin>
                  <Navigate to="/admin?tab=productivity" replace />
                </RequireAdmin>
              }
            />
            <Route path="/marketplace" element={
              <ProtectedRoute user={true}>
                <Marketplace />
              </ProtectedRoute>
            } />
            <Route path="/chapters" element={
              <ProtectedRoute user={true}>
                <Chapters />
              </ProtectedRoute>
            } />
            <Route path="/squads" element={
              <ProtectedRoute user={true}>
                <Squads />
              </ProtectedRoute>
            } />
            <Route path="/battle" element={
              <ProtectedRoute user={true}>
                <Battle />
              </ProtectedRoute>
            } />
            <Route path="/artifacts" element={
              <ProtectedRoute user={true}>
                <Artifacts />
              </ProtectedRoute>
            } />
            <Route path="/story" element={
              <ProtectedRoute user={true}>
                <Story />
              </ProtectedRoute>
            } />
            <Route path="/story/:episodeId/battle" element={
              <ProtectedRoute user={true}>
                <StoryEpisodeBattle />
              </ProtectedRoute>
            } />
            <Route path="/scorekeeper" element={
              <ProtectedRoute user={true}>
                <ScorekeeperInterface />
              </ProtectedRoute>
            } />
            <Route path="/assessment-goals" element={
              <ProtectedRoute user={true}>
                <AssessmentGoalsStudent />
              </ProtectedRoute>
            } />
            <Route path="/weekly-goals" element={
              <ProtectedRoute user={true}>
                <WeeklyGoalsPage />
              </ProtectedRoute>
            } />
            <Route path="/work-board" element={
              <ProtectedRoute user={true}>
                <WorkBoardPage />
              </ProtectedRoute>
            } />
            <Route path="/island-raid" element={
              <ProtectedRoute user={true}>
                <IslandRaid />
              </ProtectedRoute>
            } />
            <Route path="/island-raid/lobby/:lobbyId" element={
              <ProtectedRoute user={true}>
                <IslandRaidLobby />
              </ProtectedRoute>
            } />
            <Route path="/island-raid/game/:gameId" element={
              <ProtectedRoute user={true}>
                <IslandRaidGame />
              </ProtectedRoute>
            } />
            {/* Legacy routes for backwards compatibility */}
            <Route path="/island-run" element={
              <ProtectedRoute user={true}>
                <IslandRaid />
              </ProtectedRoute>
            } />
            <Route path="/island-run/lobby/:lobbyId" element={
              <ProtectedRoute user={true}>
                <IslandRaidLobby />
              </ProtectedRoute>
            } />
            <Route path="/island-run/game/:gameId" element={
              <ProtectedRoute user={true}>
                <IslandRaidGame />
              </ProtectedRoute>
            } />
            {/* Live Events - Primary routes */}
            <Route path="/live-events" element={
              <ProtectedRoute user={true}>
                <LiveEvents />
              </ProtectedRoute>
            } />
            <Route path="/live-events/:eventId" element={
              <ProtectedRoute user={true}>
                <InSessionBattleView />
              </ProtectedRoute>
            } />
            
            {/* In-Session routes - Backward compatibility */}
            <Route path="/in-session" element={
              <ProtectedRoute user={true}>
                <Navigate to="/live-events" replace />
              </ProtectedRoute>
            } />
            <Route path="/in-session/room/:roomId" element={
              <ProtectedRoute user={true}>
                <InSessionRoom />
              </ProtectedRoute>
            } />
            <Route path="/in-session/create" element={
              <ProtectedRoute user={true}>
                <InSessionCreate />
              </ProtectedRoute>
            } />
            <Route path="/in-session/:sessionId" element={
              <ProtectedRoute user={true}>
                <InSessionBattleView />
              </ProtectedRoute>
            } />
            <Route path="/training-grounds" element={
              <ProtectedRoute user={true}>
                <TrainingGrounds />
              </ProtectedRoute>
            } />
            <Route path="/admin/skill-library" element={
              <RequireAdmin>
                <SkillLibraryAdmin />
              </RequireAdmin>
            } />
            <Route path="/admin/skill-analytics" element={
              <RequireAdmin>
                <SkillAnalyticsAdmin />
              </RequireAdmin>
            } />
            <Route path="/training-grounds/quiz/:quizSetId" element={
              <ProtectedRoute user={true}>
                <QuizPlayer />
              </ProtectedRoute>
            } />
            <Route path="/training-grounds/results/:attemptId" element={
              <ProtectedRoute user={true}>
                <QuizResults />
              </ProtectedRoute>
            } />
            <Route path="/mission/:missionId/play" element={
              <ProtectedRoute user={true}>
                <MissionRunner />
              </ProtectedRoute>
            } />
            <Route path="/level2-manifest-builder" element={
              <ProtectedRoute user={true}>
                <Level2ManifestBuilderPage />
              </ProtectedRoute>
            } />
            {/* Catch-all route for 404 */}
            <Route path="*" element={
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '50vh',
                gap: '1rem',
                padding: '2rem',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '4rem' }}>🔍</div>
                <h2 style={{ color: '#374151', margin: 0 }}>Page Not Found</h2>
                <p style={{ color: '#6b7280', margin: 0 }}>
                  The page you're looking for doesn't exist.
                </p>
                <button
                  onClick={() => window.location.href = '/'}
                  style={{
                    backgroundColor: '#4f46e5',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: '500',
                    marginTop: '1rem'
                  }}
                >
                  Go Home
                </button>
              </div>
            } />
          </Routes>
        </RouteTransition>
      </main>
      
      {/* Global Components */}
      <TutorialManager />
      {popupControls.enableSquadInvitePopup ? <InvitationManager /> : null}
      {popupControls.enableBattleInvitePopup ? <BattleInvitationManager /> : null}
      {popupControls.enableRewardNotifications ? <RewardNotifier /> : null}
      {popupControls.enableAssessmentGoalReminder ? <AssessmentGoalsNotifier /> : null}
      {popupControls.enableAssessmentGoalResultModal ? <AssessmentGoalResultNotifier /> : null}
      {popupControls.enableLiveEventJoinBanner ? <InSessionNotification /> : null}
      <ToastContainer />
      {currentUser && <SkillActivationOverlay />}
      
      {/* Milestone Modal */}
      {currentMilestone && (
        <MilestoneModal
          isOpen={isOpen}
          onClose={closeMilestone}
          milestone={currentMilestone.milestone}
          moveName={currentMilestone.moveName}
          rewards={currentMilestone.rewards}
        />
      )}
      
      {/* Generator Earnings Modal */}
      {generatorEarnings && (
        <GeneratorEarningsModal
          isOpen={showGeneratorModal}
          onClose={() => handleModalClose('dailyGenerator')}
          daysAway={generatorEarnings.daysAway}
          ppEarned={generatorEarnings.ppEarned}
          shieldsEarned={generatorEarnings.shieldsEarned}
          generatorLevel={generatorEarnings.generatorLevel}
          ppPerDay={generatorEarnings.ppPerDay}
          shieldsPerDay={generatorEarnings.shieldsPerDay}
          previousPP={generatorEarnings.previousPP}
          previousShields={generatorEarnings.previousShields}
          newPP={generatorEarnings.newPP}
          newShields={generatorEarnings.newShields}
        />
      )}
      
      {/* Announcement Carousel */}
      <AnnouncementCarousel
        isOpen={showAnnouncementCarousel}
        onClose={() => handleModalClose('announcements')}
        announcements={announcementTypes}
        onAnnouncementSeen={handleAnnouncementSeen}
      />
      
      {/* Squad check-in reminder (first login of the day) */}
      {currentUser && (
        <SquadCheckInReminderModal
          isOpen={showSquadCheckInModal}
          onClose={() => handleModalClose('squadCheckIn')}
          userId={currentUser.uid}
        />
      )}

      {/* One-time Skill Loadout tutorial (queue on sign-in or from Help / Tutorials) */}
      {currentUser && skillLoadoutTutorialSource && (
        <SkillLoadoutTutorialModal
          isOpen={true}
          markSeenOnClose={skillLoadoutTutorialSource === 'queue'}
          onClose={() => {
            const wasQueue = skillLoadoutTutorialSource === 'queue';
            setSkillLoadoutTutorialSource(null);
            if (wasQueue) handleModalClose('skillLoadoutTutorial');
          }}
        />
      )}
      
      {/* Development Components */}
      {process.env.NODE_ENV === 'development' && <FirebaseStatus />}
      <NavigationDebugger />
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <LevelUpProvider>
            <MilestoneProvider>
              <BattleProvider>
                <StoryProvider>
                  <Router>
                    <AppContent />
                  </Router>
                </StoryProvider>
              </BattleProvider>
            </MilestoneProvider>
          </LevelUpProvider>
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
